import React, { useState, useEffect, useMemo } from "react";
import {
  Grid,
  TextField,
  Chip,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  fieldContainer: {
    width: "100%",
  },
  autocompleteInput: {
    "& .MuiOutlinedInput-root": {
      minHeight: 30,
      borderRadius: 8,
      fontSize: "0.80rem",
      backgroundColor: "rgba(255, 255, 255, 0.03)",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: `${theme.palette.primary.main}80`,
      },
      "&.Mui-focused": {
        boxShadow: `0 0 8px ${theme.palette.primary.main}40`,
      },
      "&.Mui-disabled": {
        opacity: 1,
        backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.05)" : "#F1F5F9",
        borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1",
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "#CBD5E1 !important",
        },
      },
      "& .MuiOutlinedInput-input.Mui-disabled": {
        color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.75)" : "#334155 !important",
        WebkitTextFillColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.75)" : "#334155 !important",
        fontWeight: 500,
      },
      "& .MuiOutlinedInput-input.Mui-disabled::placeholder": {
        color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.75)" : "#334155 !important",
        WebkitTextFillColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.75)" : "#334155 !important",
        opacity: 1,
        fontWeight: 500,
      }
    },
    "& .MuiAutocomplete-inputRoot[class*=\"MuiOutlinedInput-root\"]": {
      padding: "2px 6px !important",
      minHeight: 30,
    },
    "& .MuiAutocomplete-inputRoot[class*=\"MuiOutlinedInput-root\"] .MuiAutocomplete-input": {
      padding: "2px 4px !important",
      fontSize: "0.80rem",
    },
    "& .MuiOutlinedInput-input": {
      padding: "4px 8px !important",
      fontSize: "0.80rem",
    },
    "& .MuiAutocomplete-popupIndicator": {
      padding: "2px !important",
    },
    "& .MuiAutocomplete-clearIndicator": {
      padding: "2px !important",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.78rem",
    }
  },
  tagChip: {
    margin: 1,
    fontSize: "0.68rem",
    height: 18,
    fontWeight: "bold",
    color: "#fff",
    textShadow: "0 1px 2px rgba(0,0,0,0.8)",
  }
}));

export function TicketsKanbanFilter({
  brandId,
  kanbanBoardId,
  kanbanLaneId,
  whatsappId,
  laneTagIds = [],
  onFiltered,
  onReset
}) {
  const theme = useTheme();
  const classes = useStyles();

  const [brands, setBrands] = useState([]);
  const [boards, setBoards] = useState([]);
  const [allWhatsapps, setAllWhatsapps] = useState([]);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      try {
        const [brandsRes, boardsRes, whatsappsRes] = await Promise.all([
          api.get("/brands"),
          api.get("/kanban-boards"),
          api.get("/whatsapp")
        ]);

        if (!isMounted) return;

        const fetchedBrands = brandsRes.data?.brands || brandsRes.data || [];
        const fetchedBoards = boardsRes.data || [];
        const fetchedWhatsapps = (whatsappsRes.data || []).map((w) => ({
          id: w.id,
          name: w.name,
          brandId: w.brandId,
          channel: w.channel
        }));

        setBrands(fetchedBrands);
        setBoards(fetchedBoards);
        setAllWhatsapps(fetchedWhatsapps);
      } catch (err) {
        toastError(err);
      } finally {
        if (isMounted) setLoadingData(false);
      }
    };

    loadInitialData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Embudos filtrados por la marca seleccionada
  const filteredBoards = useMemo(() => {
    if (!brandId) return [];
    return boards.filter((b) => Number(b.brandId) === Number(brandId));
  }, [brandId, boards]);

  // Embudo actualmente seleccionado
  const currentBoard = useMemo(() => {
    if (!kanbanBoardId) return null;
    return boards.find((b) => Number(b.id) === Number(kanbanBoardId)) || null;
  }, [kanbanBoardId, boards]);

  // Etapas del embudo seleccionado (ya vienen ordenadas por laneOrder desde el backend)
  const availableLanes = useMemo(() => {
    if (!currentBoard || !Array.isArray(currentBoard.tags)) return [];
    return currentBoard.tags;
  }, [currentBoard]);

  // Etapa actualmente seleccionada
  const currentLane = useMemo(() => {
    if (!kanbanLaneId || !availableLanes) return null;
    return availableLanes.find((l) => Number(l.id) === Number(kanbanLaneId)) || null;
  }, [kanbanLaneId, availableLanes]);

  // Sub-etiquetas disponibles para la etapa seleccionada
  const availableLaneTags = useMemo(() => {
    if (!currentLane || !Array.isArray(currentLane.kanbanLaneTags)) return [];
    return currentLane.kanbanLaneTags
      .map((klt) => klt.tag)
      .filter((t) => t && t.id);
  }, [currentLane]);

  // Conexiones contextualizadas según el embudo o la marca
  const availableWhatsapps = useMemo(() => {
    if (currentBoard && Array.isArray(currentBoard.whatsapps) && currentBoard.whatsapps.length > 0) {
      return currentBoard.whatsapps.map((w) => ({
        id: w.id,
        name: w.name,
        channel: w.channel
      }));
    }
    if (brandId) {
      return allWhatsapps.filter((w) => Number(w.brandId) === Number(brandId));
    }
    return allWhatsapps;
  }, [currentBoard, brandId, allWhatsapps]);

  const selectedBrandObj = brands.find((b) => Number(b.id) === Number(brandId)) || null;
  const selectedBoardObj = filteredBoards.find((b) => Number(b.id) === Number(kanbanBoardId)) || null;
  const selectedLaneObj = availableLanes.find((l) => Number(l.id) === Number(kanbanLaneId)) || null;
  const selectedWhatsappObj = availableWhatsapps.find((w) => Number(w.id) === Number(whatsappId)) || null;
  const selectedLaneTagsObjs = availableLaneTags.filter((t) =>
    Array.isArray(laneTagIds) && laneTagIds.map(Number).includes(Number(t.id))
  );

  // Manejador del cambio de Marca
  const handleBrandChange = (e, newBrand) => {
    const newBrandId = newBrand ? newBrand.id : "";
    onFiltered({
      brandId: newBrandId,
      brandName: newBrand ? newBrand.name : "",
      kanbanBoardId: "",
      boardName: "",
      kanbanLaneId: "",
      laneName: "",
      whatsappId: "",
      whatsappName: "",
      laneTagIds: []
    });
  };

  // Manejador del cambio de Embudo
  const handleBoardChange = (e, newBoard) => {
    const newBoardId = newBoard ? newBoard.id : "";
    onFiltered({
      brandId,
      brandName: selectedBrandObj?.name || "",
      kanbanBoardId: newBoardId,
      boardName: newBoard ? newBoard.name : "",
      kanbanLaneId: "",
      laneName: "",
      whatsappId: "",
      whatsappName: "",
      laneTagIds: []
    });
  };

  // Manejador del cambio de Etapa
  const handleLaneChange = (e, newLane) => {
    const newLaneId = newLane ? newLane.id : "";
    onFiltered({
      brandId,
      brandName: selectedBrandObj?.name || "",
      kanbanBoardId,
      boardName: selectedBoardObj?.name || "",
      kanbanLaneId: newLaneId,
      laneName: newLane ? newLane.name : "",
      whatsappId,
      whatsappName: selectedWhatsappObj?.name || "",
      laneTagIds: []
    });
  };

  // Manejador del cambio de Conexión
  const handleWhatsappChange = (e, newWhatsapp) => {
    const newWpId = newWhatsapp ? newWhatsapp.id : "";
    onFiltered({
      brandId,
      brandName: selectedBrandObj?.name || "",
      kanbanBoardId,
      boardName: selectedBoardObj?.name || "",
      kanbanLaneId,
      laneName: selectedLaneObj?.name || "",
      whatsappId: newWpId,
      whatsappName: newWhatsapp ? newWhatsapp.name : "",
      laneTagIds
    });
  };

  // Manejador del cambio de Sub-etiquetas de Etapa
  const handleLaneTagsChange = (e, newTags) => {
    const selectedIds = (newTags || []).map((t) => t.id);
    onFiltered({
      brandId,
      brandName: selectedBrandObj?.name || "",
      kanbanBoardId,
      boardName: selectedBoardObj?.name || "",
      kanbanLaneId,
      laneName: selectedLaneObj?.name || "",
      whatsappId,
      whatsappName: selectedWhatsappObj?.name || "",
      laneTagIds: selectedIds
    });
  };

  return (
    <Grid container spacing={2}>
      {/* 1. Selector de Marca */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          options={brands}
          value={selectedBrandObj}
          onChange={handleBrandChange}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, value) => Number(option.id) === Number(value.id)}
          disabled={loadingData}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("tickets.advancedFilters.brand")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder={i18n.t("tickets.advancedFilters.brandPlaceholder")}
              className={classes.autocompleteInput}
            />
          )}
        />
      </Grid>

      {/* 2. Selector de Embudo */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          options={filteredBoards}
          value={selectedBoardObj}
          onChange={handleBoardChange}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, value) => Number(option.id) === Number(value.id)}
          disabled={!brandId || filteredBoards.length === 0}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("tickets.advancedFilters.funnel")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder={
                !brandId
                  ? i18n.t("tickets.advancedFilters.funnelLockedPlaceholder")
                  : filteredBoards.length === 0
                  ? i18n.t("tickets.advancedFilters.funnelEmptyPlaceholder")
                  : i18n.t("tickets.advancedFilters.funnelPlaceholder")
              }
              className={classes.autocompleteInput}
            />
          )}
        />
      </Grid>

      {/* 3. Selector de Etapa del Embudo */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          options={availableLanes}
          value={selectedLaneObj}
          onChange={handleLaneChange}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, value) => Number(option.id) === Number(value.id)}
          disabled={!kanbanBoardId || availableLanes.length === 0}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("tickets.advancedFilters.stage")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder={
                !kanbanBoardId
                  ? i18n.t("tickets.advancedFilters.stageLockedPlaceholder")
                  : availableLanes.length === 0
                  ? i18n.t("tickets.advancedFilters.stageEmptyPlaceholder")
                  : i18n.t("tickets.advancedFilters.stagePlaceholder")
              }
              className={classes.autocompleteInput}
            />
          )}
        />
      </Grid>

      {/* 4. Selector de Conexión (WhatsApp) Contextualizada */}
      <Grid item xs={12} sm={6}>
        <Autocomplete
          size="small"
          options={availableWhatsapps}
          value={selectedWhatsappObj}
          onChange={handleWhatsappChange}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, value) => Number(option.id) === Number(value.id)}
          disabled={availableWhatsapps.length === 0}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("tickets.advancedFilters.connection")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder={
                availableWhatsapps.length === 0
                  ? i18n.t("tickets.advancedFilters.connectionEmptyPlaceholder")
                  : i18n.t("tickets.advancedFilters.connectionAllPlaceholder")
              }
              className={classes.autocompleteInput}
            />
          )}
        />
      </Grid>

      {/* 5. Sub-etiquetas de la Etapa */}
      <Grid item xs={12}>
        <Autocomplete
          multiple
          size="small"
          options={availableLaneTags}
          value={selectedLaneTagsObjs}
          onChange={handleLaneTagsChange}
          getOptionLabel={(option) => option.name || ""}
          getOptionSelected={(option, value) => Number(option.id) === Number(value.id)}
          disabled={!kanbanLaneId || availableLaneTags.length === 0}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.id}
                label={option.name}
                size="small"
                className={classes.tagChip}
                style={{
                  backgroundColor: option.color || theme.palette.primary.main,
                }}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("tickets.advancedFilters.subtags")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              placeholder={
                !kanbanLaneId
                  ? i18n.t("tickets.advancedFilters.subtagsLockedPlaceholder")
                  : availableLaneTags.length === 0
                  ? i18n.t("tickets.advancedFilters.subtagsEmptyPlaceholder")
                  : i18n.t("tickets.advancedFilters.subtagsPlaceholder")
              }
              className={classes.autocompleteInput}
            />
          )}
        />
      </Grid>
    </Grid>
  );
}

export default TicketsKanbanFilter;
